/**
 * WorkerSection - Pending withdrawals for worker approval
 *
 * Uses full-page detail view for processing withdrawals:
 * - List view: Shows all pending/paid withdrawals
 * - Detail view: Full page with action buttons
 *
 * Flow:
 * - Initial: [Auto Cascade] [Manual] [Edit] [Reject]
 * - After cascade: [Check] → [Confirm]
 * - Manual: Photo upload → [Confirm]
 */

import { useState, useCallback, useEffect } from 'react';
import { RefreshCw, ArrowUpFromLine } from 'lucide-react';
import { InfoCard, InfoCardList } from '@/components/shared';
import { StaffLayout } from '@/components/layout';
import { cn } from '@/lib/utils';
import { WorkerWithdrawalPage } from './WorkerWithdrawalPage';

// ============================================
// TYPES
// ============================================

export interface WorkerWithdrawal {
  id: string;
  referenceNumber: string;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  amount: number;
  actualPayout: number | null;
  bonusDeducted: number | null;
  editedAmount: number | null;
  cardNumber: string | null;
  cardHolderName: string | null;
  cardBank: string | null;
  status: string;
  providerTransactionId: string | null;
  createdAt: string;
  isLocked: boolean;
  lockedBy: string | null;
}

export interface WorkerSectionProps {
  /** List of pending withdrawals */
  withdrawals: WorkerWithdrawal[];
  /** Loading state */
  isLoading?: boolean;
  /** Current user ID (for lock attribution) */
  currentUserId?: string;
  /** Refresh handler */
  onRefresh?: () => void;
  /** Lock withdrawal (acquire for processing) */
  onLock?: (id: string) => Promise<void>;
  /** Auto cascade handler - returns success and status */
  onAutoCascade?: (id: string) => Promise<{ success: boolean; status?: string; error?: string }>;
  /** Check status handler */
  onCheckStatus?: (id: string) => Promise<{ success: boolean; status?: string }>;
  /** Confirm complete handler (after paid status) */
  onConfirmComplete?: (id: string) => Promise<void>;
  /** Manual complete handler */
  onManualComplete?: (id: string, photo: File) => Promise<void>;
  /** Edit amount handler */
  onEditAmount?: (id: string, newAmount: number, reason: string) => Promise<void>;
  /** Reject handler */
  onReject?: (id: string, reason: string) => Promise<void>;
  /** Check if current user owns the lock */
  isOwnLock?: (lockedBy: string | null) => boolean;
}

// ============================================
// COMPONENT
// ============================================

export function WorkerSection({
  withdrawals,
  isLoading = false,
  currentUserId,
  onRefresh,
  onLock,
  onAutoCascade,
  onCheckStatus,
  onConfirmComplete,
  onManualComplete,
  onEditAmount,
  onReject,
  isOwnLock = () => true,
}: WorkerSectionProps) {
  // View state: list or detail
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WorkerWithdrawal | null>(null);
  const [lockingId, setLockingId] = useState<string | null>(null);

  // Handle withdrawal click - lock and open detail
  const handleWithdrawalClick = useCallback(async (withdrawal: WorkerWithdrawal) => {
    // If already locked by us, just open
    if (withdrawal.isLocked && isOwnLock(withdrawal.lockedBy)) {
      setSelectedWithdrawal(withdrawal);
      return;
    }

    // If not locked, acquire lock first
    if (!withdrawal.isLocked && onLock) {
      setLockingId(withdrawal.id);
      try {
        await onLock(withdrawal.id);
        // Update withdrawal with lock info and open
        setSelectedWithdrawal({
          ...withdrawal,
          isLocked: true,
          lockedBy: currentUserId || 'current-user',
        });
      } catch (error) {
        // Lock failed, don't open detail
        console.error('Failed to acquire lock:', error);
      } finally {
        setLockingId(null);
      }
      return;
    }

    // Locked by someone else - don't open
    if (withdrawal.isLocked && !isOwnLock(withdrawal.lockedBy)) {
      return;
    }
  }, [onLock, currentUserId, isOwnLock]);

  // Handle back from detail
  const handleBack = useCallback(() => {
    setSelectedWithdrawal(null);
    // Refresh list when coming back
    onRefresh?.();
  }, [onRefresh]);

  // Cascade handler for detail page
  const handleAutoCascade = useCallback(async () => {
    if (!selectedWithdrawal || !onAutoCascade) {
      return { success: false };
    }
    const result = await onAutoCascade(selectedWithdrawal.id);
    if (result.success) {
      // Update local withdrawal status
      setSelectedWithdrawal(prev => prev ? { ...prev, status: 'processing' } : null);
    }
    return result;
  }, [selectedWithdrawal, onAutoCascade]);

  // Check status handler for detail page
  const handleCheckStatus = useCallback(async () => {
    if (!selectedWithdrawal || !onCheckStatus) {
      return { success: false };
    }
    const result = await onCheckStatus(selectedWithdrawal.id);
    if (result.success && result.status === 'completed') {
      // Update local withdrawal status
      setSelectedWithdrawal(prev => prev ? { ...prev, status: 'paid' } : null);
    }
    return result;
  }, [selectedWithdrawal, onCheckStatus]);

  // Confirm complete handler for detail page
  const handleConfirmComplete = useCallback(async () => {
    if (!selectedWithdrawal || !onConfirmComplete) return;
    await onConfirmComplete(selectedWithdrawal.id);
  }, [selectedWithdrawal, onConfirmComplete]);

  // Manual complete handler for detail page
  const handleManualComplete = useCallback(async (photo: File) => {
    if (!selectedWithdrawal || !onManualComplete) return;
    await onManualComplete(selectedWithdrawal.id, photo);
  }, [selectedWithdrawal, onManualComplete]);

  // Edit amount handler for detail page
  const handleEditAmount = useCallback(async (newAmount: number, reason: string) => {
    if (!selectedWithdrawal || !onEditAmount) return;
    await onEditAmount(selectedWithdrawal.id, newAmount, reason);
    // Update local withdrawal
    setSelectedWithdrawal(prev => prev ? { ...prev, editedAmount: newAmount } : null);
  }, [selectedWithdrawal, onEditAmount]);

  // Reject handler for detail page
  const handleReject = useCallback(async (reason: string) => {
    if (!selectedWithdrawal || !onReject) return;
    await onReject(selectedWithdrawal.id, reason);
  }, [selectedWithdrawal, onReject]);

  // Auto-refresh list every 10 seconds (only on list view)
  useEffect(() => {
    if (selectedWithdrawal || !onRefresh) return;
    const interval = setInterval(onRefresh, 10000);
    return () => clearInterval(interval);
  }, [selectedWithdrawal, onRefresh]);

  // If withdrawal selected, show detail page
  if (selectedWithdrawal) {
    return (
      <WorkerWithdrawalPage
        withdrawal={selectedWithdrawal}
        onBack={handleBack}
        onAutoCascade={handleAutoCascade}
        onCheck={handleCheckStatus}
        onConfirm={handleConfirmComplete}
        onManualComplete={handleManualComplete}
        onEdit={handleEditAmount}
        onReject={handleReject}
      />
    );
  }

  // List view
  return (
    <StaffLayout>
      <div className="flex flex-col h-full bg-slate-100">
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Pending Withdrawals</h1>
              <p className="text-sm text-slate-500">
                {withdrawals.length} withdrawal{withdrawals.length !== 1 ? 's' : ''} waiting
              </p>
            </div>
            <button
              type="button"
              onClick={onRefresh}
              disabled={isLoading}
              className={cn(
                'p-2.5 rounded-full',
                'bg-white border border-slate-200',
                'shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
                'transition-all',
                isLoading && 'opacity-50',
              )}
            >
              <RefreshCw className={cn('w-5 h-5 text-slate-600', isLoading && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isLoading && withdrawals.length === 0 ? (
            // Loading skeleton
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-14 rounded-full bg-white animate-pulse" />
              ))}
            </div>
          ) : withdrawals.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ArrowUpFromLine className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-400 text-sm">No pending withdrawals</p>
            </div>
          ) : (
            // Withdrawal list
            <InfoCardList>
              {withdrawals.map((w) => {
                const isLocking = lockingId === w.id;
                const isLockedByOther = w.isLocked && !isOwnLock(w.lockedBy);

                return (
                  <div
                    key={w.id}
                    className={cn(
                      isLockedByOther && 'opacity-50',
                      isLocking && 'animate-pulse'
                    )}
                  >
                    <InfoCard
                      label={`${w.username ? `@${w.username}` : w.firstName || 'User'} · ${w.editedAmount || w.actualPayout || w.amount} AZN`}
                      type="withdrawal"
                      searchHighlights={
                        isLocking
                          ? ['Locking...']
                          : w.isLocked
                            ? [`Locked: ${w.lockedBy?.slice(0, 8)}...`]
                            : undefined
                      }
                      onClick={isLockedByOther || isLocking ? undefined : () => handleWithdrawalClick(w)}
                    />
                  </div>
                );
              })}
            </InfoCardList>
          )}
        </div>
      </div>
    </StaffLayout>
  );
}
